import {createEstablishedAhpSessionRuntime} from './established-session'
import {
  copyClientCommand,
  clientCommandObserver,
  clientStopObserver,
  withClientStopObserver,
  type ClientCommandObserver,
} from './client-interaction-observer'
import {observeClientInteraction} from '@github-ui/ahp-relay/relay-command-observation'
import type {
  EstablishRequest,
  EstablishedAhpBootstrapState,
  EstablishedAhpSession,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionBootstrapAdapter,
  EstablishedAhpSessionError,
  EstablishedAhpSessionProvisioned,
  EstablishedAhpResumptionAttempt,
  EstablishedAhpPlanningPolicy,
  FirstTurnHandoff,
  StopOutcome,
} from './contracts'
import {EstablishedAhpSessionError as SessionError} from './contracts'
import {relayFailureFromError} from '@github-ui/ahp-relay/relay-failure'
import {createEstablishedAhpResumptionDeadline} from './resumption-deadline'
import {SubmissionRecoveryError, submissionRecoveryFromError, isSubmissionRecoveryEnabled} from './submission-recovery'

function firstTurnFor(request: EstablishRequest): FirstTurnHandoff | undefined {
  if (request.mode === 'provision') return request.input.firstTurn
  if (request.mode === 'recover' || request.mode === 'resume-provision') return request.firstTurn
  return undefined
}

interface PendingFirstTurn {
  readonly handoff: FirstTurnHandoff
  cancellationRequested: boolean
  cancellationSent: boolean
  cancellationFinished: boolean
  cancellationRetried: boolean
  completed: boolean
  turnId?: string
  cancellationObserver?: ClientCommandObserver
  unsubscribeCancellation?: () => void
  cancellationPromise?: Promise<void>
  resolveCancellation?: () => void
}

function stateForRequest(request: EstablishRequest): EstablishedAhpBootstrapState {
  switch (request.mode) {
    case 'provision':
      return 'provisioning'
    case 'wake':
      return 'waking'
    case 'recover':
    case 'resume-provision':
      return 'reconnecting'
    case 'attach':
      return 'connecting'
  }
}

function asSessionError(
  error: unknown,
  state: EstablishedAhpBootstrapState,
  operation = 'establish',
): EstablishedAhpSessionError {
  if (error instanceof SessionError) return error
  return new SessionError(
    operation,
    state,
    error instanceof Error ? error.message : 'Unable to establish AHP session.',
    true,
    {cause: error, failure: relayFailureFromError(error)},
  )
}

/**
 * Builds a product-neutral bootstrap coordinator. Product packages supply the
 * provision/attach/wake/recover adapters; the established session runtime and
 * public session object are shared.
 */
export function createEstablishedAhpSessionBootstrap(
  adapter: EstablishedAhpSessionBootstrapAdapter,
  planning?: EstablishedAhpPlanningPolicy,
): EstablishedAhpSessionBootstrap {
  let state: EstablishedAhpBootstrapState = 'unbound'
  let session: EstablishedAhpSession | undefined
  let provisionedSession: EstablishedAhpSessionProvisioned | undefined
  let runtime: Awaited<ReturnType<typeof createEstablishedAhpSessionRuntime>> | undefined
  let disposed = false
  let generation = 0
  let establishing: Promise<EstablishedAhpSession> | undefined
  let establishingGeneration: number | undefined
  let firstTurnWork: Promise<EstablishedAhpSession> | undefined
  let firstTurnGeneration: number | undefined
  let firstTurnCompleted = false
  let unsubscribeFirstTurnRecovery: (() => void) | undefined
  let invalidateFirstTurn: ((error: EstablishedAhpSessionError) => void) | undefined
  let firstTurnQueue: PendingFirstTurn[] = []
  const retainedCancellations = new Set<PendingFirstTurn>()
  let activeResumptionAttempt: EstablishedAhpResumptionAttempt | undefined

  const setState = (next: EstablishedAhpBootstrapState) => {
    state = next
  }

  const clearFirstTurnRecovery = () => {
    unsubscribeFirstTurnRecovery?.()
    unsubscribeFirstTurnRecovery = undefined
  }

  const completeFirstTurn = (establishedSession: EstablishedAhpSession) => {
    clearFirstTurnRecovery()
    session = establishedSession
    setState('established')
    firstTurnCompleted = true
    runtime?.activateResumptionRecovery()
  }

  const cleanupFirstTurnQueue = () => {
    if (!firstTurnCompleted || firstTurnQueue.some(item => item.unsubscribeCancellation)) return
    firstTurnQueue = []
  }

  const finishFirstTurnCancellation = (item: PendingFirstTurn) => {
    retainedCancellations.delete(item)
    item.cancellationFinished = true
    item.unsubscribeCancellation?.()
    item.unsubscribeCancellation = undefined
    item.resolveCancellation?.()
    item.resolveCancellation = undefined
    item.cancellationPromise = undefined
    cleanupFirstTurnQueue()
  }

  const clearFirstTurnQueue = (reason?: 'reset' | 'disposed') => {
    const items = reason ? [...firstTurnQueue, ...retainedCancellations] : firstTurnQueue
    for (const item of items) {
      if (reason) {
        observeClientInteraction(() => clientCommandObserver(item.handoff.prompt)?.disposition?.(reason))
        observeClientInteraction(() => item.cancellationObserver?.disposition?.(reason))
      }
      finishFirstTurnCancellation(item)
    }
    firstTurnQueue = []
  }

  const cancellationPromiseFor = (item: PendingFirstTurn): Promise<void> => {
    if (!item.cancellationRequested || item.cancellationSent || item.cancellationFinished) return Promise.resolve()
    if (!item.cancellationPromise) {
      item.cancellationPromise = new Promise(resolve => {
        item.resolveCancellation = resolve
      })
    }
    return item.cancellationPromise
  }

  const attemptFirstTurnCancellation = (
    item: PendingFirstTurn,
    establishedSession: EstablishedAhpSession,
    retry = false,
  ) => {
    if (!item.cancellationRequested || item.cancellationSent || item.cancellationFinished || !item.turnId) return

    const lifecycleState = establishedSession.lifecycle.getState()
    if (lifecycleState === 'connecting' || lifecycleState === 'reconnecting') {
      if (item.cancellationRetried) {
        finishFirstTurnCancellation(item)
        return
      }
      if (item.unsubscribeCancellation) return

      item.unsubscribeCancellation = establishedSession.lifecycle.subscribe(() => {
        const nextState = establishedSession.lifecycle.getState()
        if (nextState === 'live') {
          item.unsubscribeCancellation?.()
          item.unsubscribeCancellation = undefined
          attemptFirstTurnCancellation(item, establishedSession, true)
        } else if (nextState === 'failed' || nextState === 'disposed') {
          finishFirstTurnCancellation(item)
        }
      })
      return
    }
    if (lifecycleState !== 'live') {
      finishFirstTurnCancellation(item)
      return
    }

    const firstTurnSettled =
      establishedSession.view.getSnapshot().state?.turns.some(turn => turn.id === item.turnId) === true
    if (firstTurnSettled) {
      finishFirstTurnCancellation(item)
      return
    }

    if (retry) item.cancellationRetried = true

    const targetTurnId = item.turnId
    observeClientInteraction(() => item.cancellationObserver?.bindTarget?.(establishedSession.identity, targetTurnId))
    const outcome = withClientStopObserver(item.cancellationObserver, () =>
      establishedSession.commands.cancel(item.turnId),
    )
    if (outcome.accepted) {
      item.cancellationSent = true
      finishFirstTurnCancellation(item)
    } else {
      const nextLifecycleState = establishedSession.lifecycle.getState()
      if (nextLifecycleState === 'connecting' || nextLifecycleState === 'reconnecting') {
        attemptFirstTurnCancellation(item, establishedSession)
      } else {
        finishFirstTurnCancellation(item)
      }
    }
  }

  const establishAttempt = async (request: EstablishRequest): Promise<EstablishedAhpSession> => {
    const establishingState = stateForRequest(request)
    const attemptGeneration = ++generation
    const resumptionDeadline =
      request.mode === 'attach' || request.mode === 'wake' || request.mode === 'recover'
        ? (request.resumptionAttempt ?? createEstablishedAhpResumptionDeadline())
        : undefined
    activeResumptionAttempt = resumptionDeadline
    const waitForResumption = async <T>(work: () => Promise<T>) => {
      if (!resumptionDeadline) return work()
      const signal = resumptionDeadline.signal
      if (signal.aborted) throw signal.reason
      return resumptionDeadline.waitFor(work())
    }
    let attemptRuntime: Awaited<ReturnType<typeof createEstablishedAhpSessionRuntime>> | undefined

    const ensureCurrent = () => {
      if (disposed) throw new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false)
      if (attemptGeneration !== generation) {
        throw new SessionError('establish', establishingState, 'AHP bootstrap attempt was superseded.', true)
      }
    }

    try {
      if (disposed) throw new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false)
      if (session && state === 'established') return session
      setState(establishingState)

      const provisioned =
        request.mode === 'provision'
          ? await adapter.provision(request.input)
          : request.mode === 'resume-provision'
            ? provisionedSession
            : request.mode === 'attach'
              ? await waitForResumption(() => adapter.attach(request.identity))
              : request.mode === 'wake'
                ? await waitForResumption(() => adapter.wake(request.identity))
                : await waitForResumption(() => adapter.recover(request.identity))

      ensureCurrent()
      if (!provisioned) {
        throw new SessionError(
          'establish',
          establishingState,
          'No provisioned AHP session is available to resume.',
          false,
        )
      }
      if (request.mode === 'provision') {
        provisionedSession = provisioned
        request.onIdentityResolved?.(provisioned.identity)
      }
      attemptRuntime = await createEstablishedAhpSessionRuntime({
        ...provisioned,
        ...(planning ? {planning} : {}),
        ...(resumptionDeadline ? {resumptionDeadline} : {}),
        ...('awaitIntentAfterLive' in request && request.awaitIntentAfterLive ? {awaitIntentAfterLive: true} : {}),
        ...('connectionAdmission' in request ? {connectionAdmission: request.connectionAdmission} : {}),
      })
      ensureCurrent()
      runtime = attemptRuntime
      await attemptRuntime.start()
      ensureCurrent()

      const establishedSession = attemptRuntime.session
      if (!firstTurnFor(request)) {
        attemptRuntime.activateResumptionRecovery()
        session = establishedSession
        setState('established')
      }
      return establishedSession
    } catch (error) {
      attemptRuntime?.dispose()
      if (attemptGeneration === generation && !disposed) {
        if (runtime === attemptRuntime) runtime = undefined
        session = undefined
        setState('failed')
      }
      throw asSessionError(error, state)
    } finally {
      resumptionDeadline?.dispose()
      if (activeResumptionAttempt === resumptionDeadline) activeResumptionAttempt = undefined
      if (establishingGeneration === attemptGeneration) {
        establishing = undefined
        establishingGeneration = undefined
      }
    }
  }

  const establishRuntime = (request: EstablishRequest): Promise<EstablishedAhpSession> => {
    if (disposed) return Promise.reject(new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false))
    if (firstTurnWork) return firstTurnWork
    if (session && state === 'established') {
      // Don't block the cached session on recovery; a liveness probe can take seconds.
      const activeRuntime = runtime
      void (async () => {
        try {
          await activeRuntime?.wakeIfReconnecting(
            'connectionAdmission' in request ? request.connectionAdmission : undefined,
          )
        } catch {
          // The runtime's own reconnect path reports failures.
        }
      })()
      return Promise.resolve(session)
    }
    if (establishing) return establishing

    const attempt = establishAttempt(request)
    establishingGeneration = generation
    establishing = attempt
    return attempt
  }

  const dispatchFirstTurn = (
    firstTurn: FirstTurnHandoff,
    established: Promise<EstablishedAhpSession>,
  ): Promise<EstablishedAhpSession> => {
    if (firstTurnCompleted && session && state === 'established') return established
    const existingItem = firstTurnQueue.find(item => item.handoff.requestId === firstTurn.requestId)
    if (!existingItem && (!firstTurnWork || firstTurn.origin === 'recovery')) {
      firstTurnQueue.push({
        handoff: firstTurn,
        cancellationRequested: false,
        cancellationSent: false,
        cancellationFinished: false,
        cancellationRetried: false,
        completed: false,
      })
    }
    if (firstTurnWork) return firstTurnWork

    const attemptGeneration = generation
    const attempt = (async () => {
      let dispatchStarted = false
      let retainedRuntimeForRecovery = false
      try {
        const establishedSession = await established
        if (disposed) throw new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false)
        if (attemptGeneration !== generation) {
          throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
        }
        const activeRuntime = runtime
        if (!activeRuntime) {
          throw new SessionError('establish', state, 'AHP session runtime is unavailable.', true)
        }

        const completeAcceptedFirstTurn = (item: PendingFirstTurn): boolean => {
          if (disposed || attemptGeneration !== generation) return false
          const accepted = isSubmissionRecoveryEnabled() ? establishedSession.input.recovery?.getSnapshot() : undefined
          if (accepted?.requestId === item.handoff.requestId && accepted.phase === 'accepted' && accepted.dispatch) {
            if (item.cancellationRequested) {
              item.turnId = accepted.dispatch.turnId
              attemptFirstTurnCancellation(item, establishedSession)
            }
            completeFirstTurn(establishedSession)
            return true
          }
          return firstTurnCompleted
        }
        const invalidated = Promise.withResolvers<never>()
        invalidateFirstTurn = invalidated.reject
        for (let index = 0; index < firstTurnQueue.length; index++) {
          const item = firstTurnQueue[index]
          if (!item) break
          if (completeAcceptedFirstTurn(item)) return establishedSession
          let prompt = item.handoff.prompt
          const observer = clientCommandObserver(prompt)
          if (observer) observeClientInteraction(() => observer.bind?.(establishedSession.identity))
          if (item.handoff.configureSession) {
            if (observer) observeClientInteraction(() => observer.waiting(true, 'configuration'))
            try {
              await item.handoff.configureSession(establishedSession, prompt)
            } catch (error) {
              if (completeAcceptedFirstTurn(item)) return establishedSession
              throw (
                establishedSession.input.recovery?.preparationFailed?.(prompt, error, item.handoff.requestId) ?? error
              )
            }
            if (observer) observeClientInteraction(() => observer.waiting(false, 'configuration'))
            if (attemptGeneration !== generation) {
              throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
            }
          }
          if (item.handoff.preparePrompt) {
            if (observer) observeClientInteraction(() => observer.waiting(true, 'configuration'))
            let prepared
            try {
              prepared = await item.handoff.preparePrompt(establishedSession, prompt)
            } catch (error) {
              if (completeAcceptedFirstTurn(item)) return establishedSession
              throw (
                establishedSession.input.recovery?.preparationFailed?.(prompt, error, item.handoff.requestId) ?? error
              )
            }
            if (observer) observeClientInteraction(() => observer.waiting(false, 'configuration'))
            prompt = copyClientCommand(prompt, prepared)
            if (attemptGeneration !== generation) {
              throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
            }
          }
          if (completeAcceptedFirstTurn(item)) return establishedSession
          dispatchStarted = true
          const receipt = await activeRuntime.session.input.submit(
            prompt,
            item.handoff.requestId,
            item.handoff.beforeDispatch,
            item.handoff.directTurnTelemetryContext,
          )
          if (attemptGeneration !== generation) {
            throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
          }
          item.turnId = receipt.turnId
          // Runs unguarded, like the direct-turn telemetry observers, so a broken
          // observer is visible rather than silently dropped. `dispatchStarted` is
          // already true above, so a throw here is still classified as a submit
          // failure and cannot make this dispatched prompt look safe to retry.
          try {
            item.handoff.afterDispatch?.(receipt)
          } catch (error) {
            const recovery = establishedSession.input.recovery
            if (!isSubmissionRecoveryEnabled() || !recovery?.getSnapshot()?.dispatch) throw error
            throw SubmissionRecoveryError.forAttempt(recovery, recovery.getSnapshot(), error)
          }
          if (receipt.turnId) {
            try {
              item.handoff.onTurnDispatched?.(receipt.turnId)
            } catch (error) {
              // Dispatch already succeeded, so a best-effort observer must not make the turn retriable.
              // eslint-disable-next-line no-console
              console.error('AHP first-turn onTurnDispatched observer threw', error)
            }
          }
          attemptFirstTurnCancellation(item, establishedSession)
          await Promise.race([activeRuntime.waitForAccepted(receipt), invalidated.promise])
          if (attemptGeneration !== generation) {
            throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
          }
          item.completed = true
          if (item.cancellationRequested && index + 1 < firstTurnQueue.length) {
            await Promise.race([cancellationPromiseFor(item), invalidated.promise])
          }
        }

        completeFirstTurn(establishedSession)
        return establishedSession
      } catch (error) {
        if (attemptGeneration === generation && !disposed) {
          if (isSubmissionRecoveryEnabled() && submissionRecoveryFromError(error) && runtime) {
            session = runtime.session
            runtime.activateResumptionRecovery()
            setState('established')
            retainedRuntimeForRecovery = true
            const retainedRuntime = runtime
            const recovery = session.input.recovery
            const retainedAttempt = recovery?.getSnapshot()
            const retainedItem = firstTurnQueue.find(item => item.handoff.requestId === retainedAttempt?.requestId)
            if (recovery && retainedAttempt?.dispatch && retainedItem) {
              retainedItem.turnId = retainedAttempt.dispatch.turnId
              attemptFirstTurnCancellation(retainedItem, retainedRuntime.session)
              clearFirstTurnRecovery()
              const completeRecoveredFirstTurn = () => {
                const recoveredAttempt = recovery.getSnapshot()
                if (
                  disposed ||
                  generation !== attemptGeneration ||
                  runtime !== retainedRuntime ||
                  recoveredAttempt?.requestId !== retainedAttempt.requestId ||
                  recoveredAttempt.dispatch?.turnId !== retainedAttempt.dispatch?.turnId ||
                  recoveredAttempt.phase !== 'accepted'
                ) {
                  return
                }
                completeFirstTurn(retainedRuntime.session)
              }
              unsubscribeFirstTurnRecovery = recovery.subscribe(completeRecoveredFirstTurn)
              completeRecoveredFirstTurn()
            }
          } else {
            clearFirstTurnRecovery()
            generation++
            runtime?.dispose()
            runtime = undefined
            session = undefined
            setState('failed')
          }
        }
        throw asSessionError(error, state, dispatchStarted ? 'submit' : 'establish')
      } finally {
        if (firstTurnGeneration === attemptGeneration) {
          if (retainedRuntimeForRecovery && !firstTurnCompleted) {
            // Retain Stop separately so a later submit cannot replay this prompt.
            firstTurnQueue = firstTurnQueue.filter(item => {
              if (item.turnId && item.cancellationRequested && !item.cancellationSent && item.unsubscribeCancellation) {
                retainedCancellations.add(item)
                return false
              }
              return true
            })
          }
          const cancellationPendingLiveRetry =
            firstTurnCompleted &&
            firstTurnQueue.some(
              item => item.cancellationRequested && !item.cancellationSent && Boolean(item.unsubscribeCancellation),
            )
          if (!cancellationPendingLiveRetry) clearFirstTurnQueue()
          invalidateFirstTurn = undefined
          firstTurnWork = undefined
          firstTurnGeneration = undefined
        }
      }
    })()

    firstTurnGeneration = attemptGeneration
    firstTurnWork = attempt
    return attempt
  }

  const establish = (request: EstablishRequest): Promise<EstablishedAhpSession> => {
    const established = establishRuntime(request)
    const firstTurn = firstTurnFor(request)
    return firstTurn ? dispatchFirstTurn(firstTurn, established) : established
  }

  const cancelPendingFirstTurn = (): StopOutcome => {
    if (disposed || !firstTurnWork || firstTurnCompleted) return {accepted: false}
    const pendingTurn = firstTurnQueue.findLast(item => !item.completed && !item.cancellationRequested)
    if (!pendingTurn) return {accepted: false}
    pendingTurn.cancellationRequested = true
    observeClientInteraction(() => clientCommandObserver(pendingTurn.handoff.prompt)?.disposition?.('cancelled'))
    const observer = clientStopObserver()
    if (observer) {
      pendingTurn.cancellationObserver = observer
      observeClientInteraction(() => observer.waiting(true, 'session'))
    }
    void cancellationPromiseFor(pendingTurn)
    if (runtime) attemptFirstTurnCancellation(pendingTurn, runtime.session)
    return {
      accepted: true,
      ...(pendingTurn.turnId ? {turnId: pendingTurn.turnId} : {}),
    }
  }

  return {
    get state() {
      return state
    },
    get session() {
      return session
    },
    get provisionedIdentity() {
      return provisionedSession?.identity
    },
    establish,
    cancelPendingFirstTurn,
    reset: () => {
      if (disposed) return
      activeResumptionAttempt?.cancel(
        new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true),
      )
      activeResumptionAttempt = undefined
      invalidateFirstTurn?.(new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true))
      generation++
      establishing = undefined
      establishingGeneration = undefined
      firstTurnWork = undefined
      firstTurnGeneration = undefined
      firstTurnCompleted = false
      clearFirstTurnRecovery()
      invalidateFirstTurn = undefined
      clearFirstTurnQueue('reset')
      runtime?.reset()
      runtime = undefined
      session = undefined
      provisionedSession = undefined
      adapter.reset?.()
      setState('unbound')
    },
    dispose: () => {
      if (disposed) return
      disposed = true
      activeResumptionAttempt?.cancel(new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false))
      activeResumptionAttempt = undefined
      invalidateFirstTurn?.(new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false))
      generation++
      establishing = undefined
      establishingGeneration = undefined
      firstTurnWork = undefined
      firstTurnGeneration = undefined
      firstTurnCompleted = false
      clearFirstTurnRecovery()
      invalidateFirstTurn = undefined
      clearFirstTurnQueue('disposed')
      runtime?.dispose()
      runtime = undefined
      session = undefined
      provisionedSession = undefined
      adapter.dispose?.()
      setState('disposed')
    },
  }
}
